import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import { Rocket, Briefcase, Building2, Star, ArrowRight, GraduationCap, ShoppingBag, Stethoscope, Palette, Landmark } from 'lucide-react';

const clientTypes = [
  {
    icon: Rocket,
    title: 'Startups',
    desc: 'We help new ventures build a strong digital foundation with MVPs, brand identity, and scalable architecture.',
  },
  {
    icon: Briefcase,
    title: 'Small Businesses',
    desc: 'Grow your online presence with professional websites, e-commerce, and digital marketing that delivers results.',
  },
  {
    icon: Building2,
    title: 'Enterprises',
    desc: 'Custom software, system integration, and digital transformation for organizations ready to scale.',
  },
];

const industries = [
  { icon: Stethoscope, title: 'Healthcare', desc: 'Digital solutions for providers and patient care' },
  { icon: ShoppingBag, title: 'E-commerce', desc: 'Online retail and marketplace solutions' },
  { icon: Palette, title: 'Creative & Arts', desc: 'Portfolios, galleries, and creative platforms' },
  { icon: GraduationCap, title: 'Education', desc: 'Learning management and educational platforms' },
  { icon: Landmark, title: 'Finance', desc: 'Fintech and banking solutions' },
];

const testimonials = [
  {
    name: 'Client',
    role: 'Startup Founder',
    text: 'WicunaTech helped us launch our MVP on time. Professional, responsive, and great to work with.',
    rating: 5,
  },
  {
    name: 'Client',
    role: 'Small Business Owner',
    text: 'Our new website and branding have made a real difference. We highly recommend their services.',
    rating: 5,
  },
  {
    name: 'Client',
    role: 'Marketing Lead',
    text: 'From strategy to execution, the team delivered. We look forward to working with them again.',
    rating: 5,
  },
];

const Client = () => {
  return (
    <>
      <Navbar />

      {/* Hero */}
      <section className="relative py-16 px-4 bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900 text-white overflow-hidden">
        <div className="relative max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Our <span className="text-blue-400">Clients</span>
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            We work with startups, small businesses, and enterprises across diverse industries.
          </p>
        </div>
      </section>

      {/* Who We Work With */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Who We <span className="text-blue-600">Work With</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From early-stage startups to established enterprises—we partner with businesses at every stage.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {clientTypes.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="w-14 h-14 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Industries We <span className="text-blue-600">Serve</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Tailored digital solutions across healthcare, e-commerce, education, finance, and more.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {industries.map((item, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-6 rounded-2xl bg-gray-50 border border-gray-100 hover:border-blue-100 hover:shadow-sm transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4 bg-rose-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What Our <span className="text-blue-600">Clients Say</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We're proud of the relationships we build and the results we deliver.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(item.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed mb-6">"{item.text}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{item.name}</p>
                  <p className="text-sm text-gray-500">{item.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Become Our Next Success Story
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Join the businesses we've helped grow. Let's build something great together.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-blue-600 font-semibold px-8 py-3 rounded-lg hover:bg-blue-50 transition-colors"
          >
            Get in Touch
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Client;
